import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, MenuItem, Select,
  FormControl, Tooltip, Skeleton,
} from "@mui/material";
import ShieldRoundedIcon         from "@mui/icons-material/ShieldRounded";
import BarChartRoundedIcon        from "@mui/icons-material/BarChartRounded";
import AccountBalanceRoundedIcon  from "@mui/icons-material/AccountBalanceRounded";
import SettingsRoundedIcon        from "@mui/icons-material/SettingsRounded";
import OpenInNewIcon              from "@mui/icons-material/OpenInNew";
import KeyboardArrowLeft          from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight         from "@mui/icons-material/KeyboardArrowRight";
import { useNavigate } from "react-router-dom";
import { getData } from "../../services/apiService";
import { API_ENDPOINTS } from "../../services/apiService";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
  teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
  orange:  "#FF7900", orangeLt: "#FFF0E0",
  emerald: "#059669", emeraldLt: "#D1FAE5",
  rose:    "#E11D48", roseLt:   "#FFE4E6",
  amber:   "#D97706", amberLt:  "#FEF3C7",
  indigo:  "#4F46E5", indigoLt: "#EEF2FF",
  bg:      "#F7FAFC", border: "#E2E8F0",
  text:    "#0F172A", muted:  "#64748B",
};

// ── Status config ─────────────────────────────────────────────────────────
const STATUS_CFG = {
  filed:             { bg: T.indigoLt,  color: T.indigo  },
  under_review:      { bg: T.amberLt,   color: T.amber   },
  evidence_pending:  { bg: T.roseLt,    color: T.rose    },
  approved:          { bg: T.emeraldLt, color: T.emerald },
  rejected:          { bg: T.roseLt,    color: T.rose    },
  closed:            { bg: "#F1F5F9",   color: T.muted   },
};

// ── Plan tier config ──────────────────────────────────────────────────────
const PLAN_CFG = {
  BASIC:    { bg: T.tealLt,    color: T.teal   },
  STANDARD: { bg: T.indigoLt,  color: T.indigo },
  PREMIUM:  { bg: T.amberLt,   color: T.amber  },
};

const fmt = (s) => s?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) || "—";
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }) : "—";

// ── Status chip ───────────────────────────────────────────────────────────
const StatusChip = ({ status }) => {
  const cfg = STATUS_CFG[status] || { bg: "#F1F5F9", color: T.muted };
  return (
    <Box sx={{ display:"inline-flex", alignItems:"center", gap:0.5, px:1.2, py:0.3, borderRadius:"8px", bgcolor:cfg.bg, border:`1px solid ${cfg.color}22` }}>
      <Box sx={{ width:5, height:5, borderRadius:"50%", bgcolor:cfg.color }} />
      <Typography sx={{ fontSize:11, fontWeight:700, color:cfg.color, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{fmt(status)}</Typography>
    </Box>
  );
};

// ── Plan chip ─────────────────────────────────────────────────────────────
const PlanChip = ({ tier }) => {
  const key = (tier || "").toUpperCase();
  const cfg = PLAN_CFG[key] || { bg: T.tealLt, color: T.teal };
  return (
    <Box sx={{ display:"inline-flex", px:1.2, py:0.3, bgcolor:cfg.bg, borderRadius:"8px", border:`1px solid ${cfg.color}22` }}>
      <Typography sx={{ fontSize:11, fontWeight:700, color:cfg.color }}>{key || "—"}</Typography>
    </Box>
  );
};

// ── Filter pill ───────────────────────────────────────────────────────────
const FPill = ({ label, active, color, bg, onClick }) => (
  <Box onClick={onClick} sx={{
    px:1.6, py:0.6, borderRadius:"8px", cursor:"pointer",
    bgcolor:active?bg:"transparent", color:active?color:T.muted,
    fontWeight:active?700:500, fontSize:12.5,
    border:`1.5px solid ${active?color+"44":T.border}`,
    transition:"all 0.14s", whiteSpace:"nowrap",
    "&:hover":{ bgcolor:bg, color }, fontFamily:"'Plus Jakarta Sans',sans-serif",
  }}>{label}</Box>
);

// ── Skeleton row ──────────────────────────────────────────────────────────
const SkelRow = () => (
  <TableRow>
    {[90,130,90,80,100,100,100,50].map((w,i) => <TableCell key={i}><Skeleton width={w} height={16} /></TableCell>)}
  </TableRow>
);

// ── Nav tabs ──────────────────────────────────────────────────────────────
const NAV_TABS = [
  { label:"Claims",    icon:<ShieldRoundedIcon />,        path:"/app/protection-claims"    },
  { label:"Analytics", icon:<BarChartRoundedIcon />,      path:"/app/protection-analytics" },
  { label:"Fund",      icon:<AccountBalanceRoundedIcon />, path:"/app/protection-fund"      },
  { label:"Config",    icon:<SettingsRoundedIcon />,       path:"/app/protection-config"    },
];

// ── Main component ────────────────────────────────────────────────────────
export default function ProtectionClaims() {
  const navigate = useNavigate();
  const [claims,         setClaims]         = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [page,           setPage]           = useState(1);
  const [totalPages,     setTotalPages]     = useState(1);
  const [statusFilter,   setStatusFilter]   = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [perPage,        setPerPage]        = useState(15);

  const fetchClaims = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_ENDPOINTS.getProtectionClaims}?page=${page}&limit=${perPage}`;
      if (statusFilter)   url += `&status=${statusFilter}`;
      if (categoryFilter) url += `&category=${categoryFilter}`;
      const res = await getData(url);
      const result = res?.result || res?.data?.result;
      if (result) { setClaims(result.claims || []); setTotalPages(result.pagination?.totalPages || 1); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, statusFilter, categoryFilter, perPage]);

  useEffect(() => { fetchClaims(); }, [fetchClaims]);

  const totalClaims = claims.length;
  const activeClaims = claims.filter(c => !["closed","rejected"].includes(c.status)).length;

  return (
    <Box sx={{ bgcolor:T.bg, minHeight:"100vh", pb:4 }}>

      {/* ── Hero header ── */}
      <Box sx={{
        background:`linear-gradient(135deg,${T.teal} 0%,${T.tealDk} 100%)`,
        borderRadius:"20px", p:3, mb:3,
        display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:2,
        boxShadow:"0 4px 20px rgba(0,124,132,0.2)",
      }}>
        <Box sx={{ display:"flex", alignItems:"center", gap:2 }}>
          <Box sx={{ width:46, height:46, borderRadius:"12px", bgcolor:"rgba(255,255,255,0.18)", border:"1px solid rgba(255,255,255,0.25)", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <ShieldRoundedIcon sx={{ fontSize:24, color:"#fff" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize:22, fontWeight:800, color:"#fff", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Cargo Protection</Typography>
            <Typography sx={{ fontSize:13, color:"rgba(255,255,255,0.75)", mt:0.3 }}>Review and action insurance claims for protected shipments</Typography>
          </Box>
        </Box>
        {!loading && (
          <Box sx={{ display:"flex", gap:1.5 }}>
            {[
              { label:"Total Claims", value:totalClaims, bg:"rgba(255,255,255,0.15)", color:"#fff" },
              { label:"Active",       value:activeClaims, bg:T.amberLt, color:T.amber },
            ].map(s => (
              <Box key={s.label} sx={{ display:"flex", alignItems:"center", gap:0.8, bgcolor:s.bg, borderRadius:"10px", px:1.5, py:0.7, border:s.color==="#fff"?"1px solid rgba(255,255,255,0.25)":`1px solid ${s.color}22` }}>
                <Typography sx={{ fontSize:16, fontWeight:800, color:s.color, lineHeight:1, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{s.value}</Typography>
                <Typography sx={{ fontSize:11, fontWeight:600, color:s.color, opacity:0.85 }}>{s.label}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* ── Nav pill tabs ── */}
      <Box sx={{ display:"flex", gap:0.5, bgcolor:"white", border:`1px solid ${T.border}`, borderRadius:"14px", p:0.75, mb:3, width:"fit-content" }}>
        {NAV_TABS.map((t, i) => (
          <Box key={t.path} onClick={() => navigate(t.path)} sx={{
            display:"flex", alignItems:"center", gap:1,
            px:2.5, py:1.2, borderRadius:"10px", cursor:"pointer",
            bgcolor: i===0 ? T.teal : "transparent",
            color:   i===0 ? "#fff" : T.muted,
            fontWeight: i===0 ? 700 : 500, fontSize:13, transition:"all 0.15s",
            "&:hover":{ bgcolor:i===0?T.teal:T.tealLt, color:i===0?"#fff":T.teal },
            fontFamily:"'Plus Jakarta Sans',sans-serif",
          }}>
            {React.cloneElement(t.icon, { sx:{ fontSize:16 } })}
            {t.label}
          </Box>
        ))}
      </Box>

      {/* ── Toolbar ── */}
      <Box sx={{ display:"flex", gap:1.5, alignItems:"center", flexWrap:"wrap", mb:2.5 }}>
        {/* Status filter pills */}
        <Box sx={{ display:"flex", gap:0.75, bgcolor:"white", border:`1px solid ${T.border}`, borderRadius:"12px", p:0.5, flexWrap:"wrap" }}>
          <FPill label="All"              active={!statusFilter}                        color={T.teal}    bg={T.tealLt}    onClick={() => { setStatusFilter(""); setPage(1); }} />
          <FPill label="Filed"            active={statusFilter==="filed"}               color={T.indigo}  bg={T.indigoLt}  onClick={() => { setStatusFilter("filed"); setPage(1); }} />
          <FPill label="Under Review"     active={statusFilter==="under_review"}        color={T.amber}   bg={T.amberLt}   onClick={() => { setStatusFilter("under_review"); setPage(1); }} />
          <FPill label="Ev. Pending"      active={statusFilter==="evidence_pending"}    color={T.rose}    bg={T.roseLt}    onClick={() => { setStatusFilter("evidence_pending"); setPage(1); }} />
          <FPill label="Approved"         active={statusFilter==="approved"}            color={T.emerald} bg={T.emeraldLt} onClick={() => { setStatusFilter("approved"); setPage(1); }} />
          <FPill label="Rejected/Closed"  active={["rejected","closed"].includes(statusFilter)} color={T.muted} bg="#F1F5F9" onClick={() => { setStatusFilter("closed"); setPage(1); }} />
        </Box>

        {/* Category filter */}
        <FormControl size="small">
          <Select value={categoryFilter} displayEmpty onChange={e => { setCategoryFilter(e.target.value); setPage(1); }}
            sx={{ borderRadius:"12px", bgcolor:"white", fontSize:13, minWidth:160 }}>
            <MenuItem value=""><em style={{ color:T.muted }}>All Categories</em></MenuItem>
            <MenuItem value="damaged"       sx={{ fontSize:13 }}>Damaged</MenuItem>
            <MenuItem value="missing"       sx={{ fontSize:13 }}>Missing</MenuItem>
            <MenuItem value="wrong_delivery"sx={{ fontSize:13 }}>Wrong Delivery</MenuItem>
          </Select>
        </FormControl>

        {!loading && <Typography sx={{ fontSize:13, color:T.muted, ml:"auto" }}>{claims.length} claim{claims.length!==1?"s":""}</Typography>}
      </Box>

      {/* ── Table ── */}
      <Card sx={{ borderRadius:"16px", border:`1px solid ${T.border}`, boxShadow:"0 1px 4px rgba(15,23,42,0.06)", overflow:"hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor:T.bg }}>
                {["Claim Ref","Customer","Tracking Code","Category","Plan","Declared Value","Status","Filed",""].map(h => (
                  <TableCell key={h} sx={{ fontWeight:700, fontSize:11, color:T.muted, textTransform:"uppercase", letterSpacing:"0.4px", py:1.5, borderBottom:`2px solid ${T.border}` }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? Array.from({ length: 8 }).map((_,i) => <SkelRow key={i} />)
                : claims.length === 0
                ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py:6 }}>
                      <ShieldRoundedIcon sx={{ fontSize:40, color:T.border, mb:1, display:"block", mx:"auto" }} />
                      <Typography fontSize={13} color="text.secondary">No claims found.</Typography>
                    </TableCell>
                  </TableRow>
                )
                : claims.map(claim => {
                  const custName = claim.Customer ? `${claim.Customer.first_name||""} ${claim.Customer.last_name||""}`.trim() : "—";
                  const tracking = claim.Package?.tracking_code;
                  return (
                    <TableRow key={claim.claim_id} hover
                      sx={{ cursor:"pointer", "&:last-child td":{ border:0 }, "&:hover":{ bgcolor:T.tealLt+"33" } }}
                      onClick={() => navigate(`/app/protection-claims/${claim.claim_id}`)}>

                      {/* Claim ref */}
                      <TableCell>
                        <Typography sx={{ fontSize:12, fontWeight:700, color:T.teal, fontFamily:"monospace" }}>{claim.claim_ref}</Typography>
                      </TableCell>

                      {/* Customer */}
                      <TableCell>
                        <Typography sx={{ fontSize:13, fontWeight:600, color:T.text }}>{custName}</Typography>
                        {claim.Customer?.mobile_num && (
                          <Typography sx={{ fontSize:11, color:T.muted }}>{claim.Customer.mobile_num}</Typography>
                        )}
                      </TableCell>

                      {/* Tracking code — NEW */}
                      <TableCell>
                        {tracking ? (
                          <Box sx={{ display:"inline-flex", alignItems:"center", gap:0.5, px:1, py:0.3, bgcolor:T.tealLt, borderRadius:"7px", border:`1px solid ${T.teal}22` }}>
                            <Typography sx={{ fontSize:11, fontWeight:700, color:T.teal, fontFamily:"monospace" }}>{tracking}</Typography>
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize:12, color:T.muted }}>—</Typography>
                        )}
                      </TableCell>

                      {/* Category */}
                      <TableCell>
                        <Typography sx={{ fontSize:12, color:T.text }}>{fmt(claim.category)}</Typography>
                      </TableCell>

                      {/* Plan */}
                      <TableCell><PlanChip tier={claim.Booking?.plan_tier} /></TableCell>

                      {/* Declared value */}
                      <TableCell>
                        <Typography sx={{ fontSize:13, fontWeight:700, color:T.text }}>
                          {claim.Booking?.declared_value ? `₹${Number(claim.Booking.declared_value).toLocaleString("en-IN")}` : "—"}
                        </Typography>
                      </TableCell>

                      {/* Status */}
                      <TableCell><StatusChip status={claim.status} /></TableCell>

                      {/* Filed date */}
                      <TableCell>
                        <Typography sx={{ fontSize:12, color:T.muted }}>{fmtDate(claim.filed_at)}</Typography>
                      </TableCell>

                      {/* View */}
                      <TableCell padding="none">
                        <Tooltip title="View claim">
                          <IconButton size="small" onClick={e => { e.stopPropagation(); navigate(`/app/protection-claims/${claim.claim_id}`); }} sx={{ color:T.muted }}>
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              }
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {!loading && claims.length > 0 && (
          <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center", px:2.5, py:1.5, borderTop:`1px solid ${T.border}` }}>
            <Typography sx={{ fontSize:13, color:T.muted }}>Page {page} of {totalPages}</Typography>
            <Box sx={{ display:"flex", alignItems:"center", gap:1 }}>
              <Select value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }} size="small" sx={{ borderRadius:"10px", fontSize:13 }}>
                {[15,25,50].map(n => <MenuItem key={n} value={n}>{n} per page</MenuItem>)}
              </Select>
              <IconButton size="small" onClick={() => setPage(p => Math.max(p-1,1))} disabled={page<=1}
                sx={{ bgcolor:T.bg, border:`1px solid ${T.border}`, borderRadius:"8px" }}>
                <KeyboardArrowLeft sx={{ fontSize:18 }} />
              </IconButton>
              <Typography sx={{ fontSize:13, fontWeight:600, color:T.text, minWidth:56, textAlign:"center" }}>
                {page} / {totalPages}
              </Typography>
              <IconButton size="small" onClick={() => setPage(p => Math.min(p+1,totalPages))} disabled={page>=totalPages}
                sx={{ bgcolor:T.bg, border:`1px solid ${T.border}`, borderRadius:"8px" }}>
                <KeyboardArrowRight sx={{ fontSize:18 }} />
              </IconButton>
            </Box>
          </Box>
        )}
      </Card>
    </Box>
  );
}
